import { Post, samplePosts } from '@/data/mediaPosts';

// Environment detection - more robust check
const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox.lovable.dev');

console.log('API Service Environment Check:', {
  'import.meta.env.DEV': import.meta.env.DEV,
  'window.location.hostname': window.location.hostname,
  'isDevelopment': isDevelopment,
  'userAgent': navigator.userAgent
});

// API Response Types
interface BlogAPIPost {
  id: number;
  title: string;
  slug: string;
  author: string | null;
  publish_date: string;
  ogImage: {
    id: number;
    alt: string;
    s3url: string;
    filename: string;
    mimeType: string;
  };
}

interface BlogAPIPostDetail extends BlogAPIPost {
  meta_description: string;
  content_HTML: string;
  featuredImage: {
    id: number;
    alt: string;
    s3url: string;
    filename: string;
    mimeType: string;
  };
}

interface BlogAPIResponse {
  posts: BlogAPIPost[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalDocs: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Always use the proxy route to avoid CORS issues
const API_BASE_URL = 'https://www.bloggingmachine.io/api';
const BLOG_ID = '316';

console.log('Using API_BASE_URL:', API_BASE_URL);

// Fetch configuration for CORS
const fetchConfig: RequestInit = {
  method: 'GET',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
  mode: 'cors',
  cache: 'no-cache',
};

// Transform API post to our Post interface
const transformPost = (apiPost: BlogAPIPost, index: number = 0): Post => {
  // Calculate read time (rough estimate: 200 words per minute)
  const readTime = '5 min read'; // Default for now
  
  // Map categories based on title keywords (simple approach)
  let category = 'Founder Stories';
  const title = apiPost.title.toLowerCase();
  if (title.includes('investor') || title.includes('pitch') || title.includes('funding')) {
    category = 'Investor Insights';
  } else if (title.includes('demo') || title.includes('present')) {
    category = 'Pitch Tips';
  } else if (title.includes('valuation') || title.includes('seed')) {
    category = 'Fundraising Advice';
  }

  return {
    id: apiPost.id.toString(),
    title: apiPost.title,
    excerpt: apiPost.ogImage?.alt || 'Discover insights and strategies for AI hackathons and startup success.',
    category,
    date: new Date(apiPost.publish_date).toISOString().split('T')[0],
    readTime,
    image: apiPost.ogImage?.s3url || samplePosts[index % samplePosts.length]?.image || '/placeholder.svg',
    slug: apiPost.slug,
    popular: index < 3, // Mark first 3 as popular
  };
};

export const apiService = {
  // Fetch all posts with pagination
  async fetchPosts(page: number = 1, limit: number = 20): Promise<{ posts: Post[]; hasNextPage: boolean; pagination: any }> {
    try {
      const url = `${API_BASE_URL}/posts/site/${BLOG_ID}?page=${page}&limit=${limit}`;
      console.log('Fetching posts from:', url);
      
      const response = await fetch(url, fetchConfig);
      console.log('Response status:', response.status, 'OK:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: BlogAPIResponse = await response.json();
      console.log('API Response:', { postsCount: data.posts.length, pagination: data.pagination });
      
      const transformedPosts = data.posts.map((post, index) => {
        const transformed = transformPost(post, index);
        console.log('Transformed post:', { title: transformed.title, image: transformed.image });
        return transformed;
      });
      
      console.log('Successfully fetched', transformedPosts.length, 'posts from API');
      return {
        posts: transformedPosts,
        hasNextPage: data.pagination.hasNextPage,
        pagination: data.pagination,
      };
    } catch (error) {
      console.error('API fetch failed, falling back to sample data:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      
      // Fallback to sample data with pagination simulation
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedSamplePosts = samplePosts.slice(startIndex, endIndex);
      
      console.log('Using sample data fallback:', {
        requestedPage: page,
        limit,
        startIndex,
        endIndex,
        samplePostsLength: samplePosts.length,
        paginatedSamplePostsLength: paginatedSamplePosts.length
      });
      
      return {
        posts: paginatedSamplePosts,
        hasNextPage: endIndex < samplePosts.length,
        pagination: {
          page,
          limit,
          totalPages: Math.ceil(samplePosts.length / limit),
          totalDocs: samplePosts.length,
          hasNextPage: endIndex < samplePosts.length,
          hasPrevPage: page > 1,
        },
      };
    }
  },

  // Fetch individual post by slug
  async fetchPost(slug: string): Promise<BlogAPIPostDetail & { transformedPost: Post }> {
    try {
      const url = `${API_BASE_URL}/post/${BLOG_ID}/${slug}`;
      console.log('Fetching single post from:', url);
      
      const response = await fetch(url, fetchConfig);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: BlogAPIPostDetail = await response.json();
      const transformedPost = transformPost(data);
      console.log('Fetched single post:', { title: transformedPost.title, image: transformedPost.image });
      
      return {
        ...data,
        transformedPost,
      };
    } catch (error) {
      console.warn('API fetch failed, falling back to sample data:', error);
      
      // Fallback to sample data
      const samplePost = samplePosts.find(post => post.slug === slug);
      if (!samplePost) {
        throw new Error('Article not found.');
      }
      
      // Create mock API response structure
      const mockApiPost: BlogAPIPostDetail = {
        id: parseInt(samplePost.id),
        title: samplePost.title,
        slug: samplePost.slug,
        author: 'AI4Hack Team',
        publish_date: samplePost.date,
        meta_description: samplePost.excerpt,
        content_HTML: `<p>${samplePost.excerpt}</p><p>This is sample content for the article. In production, this would contain the full article content from the API.</p>`,
        ogImage: {
          id: parseInt(samplePost.id),
          alt: samplePost.title,
          s3url: samplePost.image,
          filename: `${samplePost.slug}.jpg`,
          mimeType: 'image/jpeg',
        },
        featuredImage: {
          id: parseInt(samplePost.id),
          alt: samplePost.title,
          s3url: samplePost.image,
          filename: `${samplePost.slug}.jpg`,
          mimeType: 'image/jpeg',
        },
      };
      
      return {
        ...mockApiPost,
        transformedPost: samplePost,
      };
    }
  },
};